import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWorldState } from '../state/WorldStateContext.jsx';
import { X } from 'lucide-react';

const TUTORIAL_STEPS = {
  LEASE_AIRCRAFT: 1,
  WATCH_FLIGHTS: 2,
  END_OF_DAY: 3,
  COMPLETE: 4,
};

export default function TutorialController() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { worldState, setWorldState } = useWorldState();

  const tutorial = worldState?.tutorial || { currentStep: 1, completed: false };
  const fleet = worldState?.fleet || worldState?.aircraft || [];
  const flights = worldState?.flights || [];
  const airline = worldState?.airline || {};
  const gameMode = worldState?.gameMode || 'simulation';
  const isSimpleMode = gameMode === 'simple' || gameMode === 'arcade';
  // Check multiple possible locations for day number
  const gameDay = worldState?.dayNumber || worldState?.gameDay || airline?.game_day || 1;

  // Skip/dismiss handler
  const handleSkipTutorial = () => {
    setWorldState(prev => ({
      ...prev,
      tutorial: { ...prev.tutorial, completed: true },
    }));
  };

  // Don't show if tutorial completed
  if (tutorial.completed) return null;

  // Don't show after Day 1
  if (gameDay > 1) return null;
  
  const fleetCount = fleet.length;
  const scheduledFlights = flights.filter(f => 
    f.status === 'scheduled' || f.status === 'boarding' || f.status === 'in_flight'
  ).length;
  const completedFlights = flights.filter(f => f.status === 'completed').length;
  const totalDayFlights = flights.length;
  
  // Determine current step based on game state
  let currentStep = TUTORIAL_STEPS.LEASE_AIRCRAFT;

  // Once they have aircraft and flights scheduled, show watch flights
  if (fleetCount >= 1 && totalDayFlights >= 1) {
    currentStep = TUTORIAL_STEPS.WATCH_FLIGHTS;
  }

  // End of day after flights complete
  if (totalDayFlights >= 1 && completedFlights >= totalDayFlights && completedFlights > 0) {
    currentStep = TUTORIAL_STEPS.END_OF_DAY;
  }
  
  // Step 1: Lease Aircraft
  if (currentStep === TUTORIAL_STEPS.LEASE_AIRCRAFT) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl relative">
          {/* Skip button */}
          <button
            onClick={handleSkipTutorial}
            className="absolute top-3 right-3 p-1 rounded hover:bg-zinc-800 transition-colors"
            title="Skip Tutorial"
          >
            <X className="w-5 h-5 text-gray-500 hover:text-white" />
          </button>
          <h2 className="text-xl font-bold text-white mb-4">{t('tutorial.gettingStarted')}</h2>

          <p className="text-gray-300 mb-6">
            {t('tutorial.welcomeTo')} <span className="text-emerald-400 font-semibold">{airline.name || t('tutorial.yourAirline')}</span>!
            {t('tutorial.toBeginOps')}
          </p>

          <div className="bg-zinc-800 rounded-lg p-4 mb-6">
            <div className="mb-3">
              <span className="text-white font-semibold">{t('tutorial.leaseAircraft')}</span>
            </div>

            <div className="w-full bg-zinc-700 rounded-full h-3 mb-2">
              <div
                className="bg-emerald-400 h-3 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, (fleetCount / 5) * 100)}%` }}
              />
            </div>
            <div className="text-sm text-gray-400">
              {t('tutorial.progress')}: <span className="text-white font-mono">{fleetCount}/5</span>
            </div>
          </div>

          <div className="bg-zinc-800/50 rounded-lg p-3 mb-6 border border-zinc-700">
            <p className="text-sm text-cyan-400">
              {t('tutorial.tip')}
            </p>
          </div>

          <div className="text-sm text-gray-400 mb-6">
            {isSimpleMode ? (
              <div>{t('tutorial.mode')}: <span className="text-emerald-400">{t('tutorial.simpleMode')}</span> - {t('tutorial.flyAnywhere')}</div>
            ) : (
              <>
                <div>{t('tutorial.startingSlots')}: <span className="text-white">PDX, SEA, BOI, RDM</span></div>
                <div>{t('tutorial.startingCrew')}: <span className="text-white">{t('tutorial.readyToFly')}</span></div>
              </>
            )}
          </div>

          <button
            onClick={() => navigate('/AircraftMarket')}
            className="w-full py-3 rounded-lg font-semibold transition-all"
            style={{ backgroundColor: '#60ffd0', color: '#000' }}
          >
            {t('tutorial.goToMarket')}
          </button>
        </div>
      </div>
    );
  }
  
  // Step 2: Watch Flights (non-blocking slim banner below header)
  if (currentStep === TUTORIAL_STEPS.WATCH_FLIGHTS) {
    const revenue = flights
      .filter(f => f.status === 'completed')
      .reduce((sum, f) => sum + (f.revenue || f.profit || 0), 0);

    return (
      <div
        className="fixed z-30"
        style={{ top: '70px', left: '16px' }} // Top left below header
      >
        <div
          className="flex items-center gap-3 px-4 py-1.5 text-sm rounded"
          style={{
            backgroundColor: 'rgba(16, 24, 24, 0.95)',
            border: '1px solid rgba(96, 255, 208, 0.3)',
            backdropFilter: 'blur(8px)'
          }}
        >
          <span className="text-emerald-400 font-medium text-xs uppercase tracking-wide">{t('tutorial.day')} 1</span>
          <span className="text-gray-500">|</span>
          <span className="text-gray-300 text-xs">
            {completedFlights}/{totalDayFlights} {t('tutorial.flights')}
          </span>
          <span className="text-gray-500">|</span>
          <span className="text-emerald-400 font-mono text-xs">
            +${(revenue / 100).toLocaleString()}
          </span>
          {/* Dismiss button */}
          <button
            onClick={handleSkipTutorial}
            className="ml-2 p-0.5 rounded hover:bg-zinc-800 transition-colors"
            title="Dismiss"
          >
            <X className="w-3 h-3 text-gray-500 hover:text-white" />
          </button>
        </div>
      </div>
    );
  }
  
  // Step 4 (End of Day) is handled by the existing end-of-day modal system
  return null;
}

export { TUTORIAL_STEPS };